import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import client, { url } from '../../lib/api/client';
import RaceScheduleList from './RaceScheduleList';
import RaceScheduleCalendar from './RaceScheduleCalendar';
import RaceScheduleModal from './RaceScheduleModal';

/* 레이스 모드 — 경기 스케줄.

   기존의 `경기시간 설정(1km/2km) + 경기 포인트 설정(클럽 단위 인원 구간)` 두 블록을
   대체한다. 거리·시간·상금·반복 요일을 경기마다 지정한다.
   상세: docs/race/제타트랙_레이스모드_작업계획서.md
*/

const Container = styled.div`
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const Head = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  padding-bottom: 15px; margin-bottom: 20px; border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h2`margin: 0; color: #333; font-size: 22px; font-weight: 700;`;

const Right = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: wrap;`;

const Tabs = styled.div`
  display: flex; gap: 4px; background: #f3f4f6; border-radius: 8px; padding: 4px;
`;

const Tab = styled.button`
  padding: 7px 15px; font-size: 14px; border: 0; border-radius: 6px; cursor: pointer;
  font-weight: ${(p) => p.on ? 700 : 500};
  color: ${(p) => p.on ? '#111827' : '#6b7280'};
  background: ${(p) => p.on ? '#fff' : 'transparent'};
  box-shadow: ${(p) => p.on ? '0 1px 2px rgba(0,0,0,.06)' : 'none'};
`;

const AddButton = styled.button`
  padding: 9px 16px; font-size: 14px; font-weight: 600;
  color: #fff; background: #2563eb; border: 1px solid #2563eb; border-radius: 7px; cursor: pointer;
  &:hover { background: #1d4ed8; }
`;

const Notice = styled.div`
  background-color: #f5f5f5; border-left: 4px solid #1976d2;
  padding: 14px 18px; margin-bottom: 20px; border-radius: 4px;
  font-size: 14px; color: #555; line-height: 1.8;
`;

const ErrorBox = styled.div`
  padding: 11px 14px; margin-bottom: 16px; border-radius: 6px; font-size: 14px;
  background: #fef2f2; color: #991b1b; border: 1px solid #fca5a5;
`;

const RunningTrackRaceModeComponent = () => {
  const { club } = useSelector(({ club }) => ({ club: club.defaultClub }));

  const [schedules, setSchedules] = useState([]);
  const [view, setView] = useState('list');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!club) return;
    try {
      const res = await client.get(`${url}/api/runningtrack/raceschedule/${club.clubID}`);
      if (res.data && res.data.result === 1) setSchedules(res.data.schedules || []);
    } catch (e) {
      console.error('경기 스케줄 로드 실패:', e);
      setError('경기 스케줄을 불러오지 못했습니다.');
    }
  }, [club]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (row) => { setEditing(row); setModalOpen(true); };

  const handleSave = async (form) => {
    if (!club) return;
    setSaving(true);
    setError('');
    try {
      const res = editing
        ? await client.patch(`${url}/api/runningtrack/raceschedule/${editing.clubRaceScheduleID}`, form)
        : await client.post(`${url}/api/runningtrack/raceschedule/${club.clubID}`, form);

      if (res.data && res.data.result === 1) {
        setModalOpen(false);
        setEditing(null);
        await load();
      } else {
        //--> 서버가 정본 판정이다. 화면에서 통과해도 여기서 막힐 수 있다.
        setError((res.data && res.data.message) || '저장하지 못했습니다.');
      }
    } catch (e) {
      setError('저장하지 못했습니다.');
    }
    setSaving(false);
  };

  const handleToggle = async (row, active) => {
    setError('');
    try {
      const res = await client.post(
        `${url}/api/runningtrack/raceschedule/${row.clubRaceScheduleID}/active`, { active });
      if (!(res.data && res.data.result === 1)) {
        setError((res.data && res.data.message) || '변경하지 못했습니다.');
      }
      await load();
    } catch (e) {
      setError('변경하지 못했습니다.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`${row.distance}m 경기를 삭제할까요?`)) return;
    setError('');
    try {
      await client.delete(`${url}/api/runningtrack/raceschedule/${row.clubRaceScheduleID}`);
      await load();
    } catch (e) {
      setError('삭제하지 못했습니다.');
    }
  };

  return (
    <Container>
      <Head>
        <Title>레이스 스케줄</Title>
        <Right>
          <Tabs>
            <Tab on={view === 'list'} onClick={() => setView('list')}>목록</Tab>
            <Tab on={view === 'cal'} onClick={() => setView('cal')}>주간 캘린더</Tab>
          </Tabs>
          <AddButton onClick={handleAdd}>＋ 경기 추가</AddButton>
        </Right>
      </Head>

      <Notice>
        경기 종료 시간은 <b>100m당 1분</b>으로 자동 계산됩니다. 완주하지 못한 참가자가 있어도 이 시각에 경기가 끝납니다.
        <br />
        시간이 겹치는 경기는 저장할 수 없습니다. 참여 인원이 <b>최소 참여 인원</b>에 미달하면 경기는 진행되지만 포인트는 지급되지 않습니다.
      </Notice>

      {error && <ErrorBox>{error}</ErrorBox>}

      {view === 'list' ? (
        <RaceScheduleList
          schedules={schedules}
          onToggle={handleToggle}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <RaceScheduleCalendar schedules={schedules} />
      )}

      <RaceScheduleModal
        open={modalOpen}
        editing={editing}
        schedules={schedules}
        saving={saving}
        onSave={handleSave}
        onClose={() => { setModalOpen(false); setEditing(null); }}
      />
    </Container>
  );
};

export default RunningTrackRaceModeComponent;
